import { ArrowLeft, Search, X } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/Input';
import { useMediaQuery } from '../../../../lib/use-media-query';
import styles from './SimpleList.module.css';

/**
 * The simple-list pattern (Admin redesign batch 2, option A of `3-hiring-setup/simple-list-pattern-reasons.html`): one
 * full-width table with its groups as bands, filter pills and a search over it, and a 380px panel on the right for the
 * row picked or the add form — one panel at a time (the critique). Used by Reasons, Sources, Custom fields, Pools and
 * tags, and Name matching.
 *
 * The panel sits beside the table above 1180px, slides over it as a drawer on a laptop or tablet, and rises as a sheet
 * on a phone (FRAME-SPEC §4 L1, §5). Over the table it takes the focus, keeps Tab inside, and gives the focus back
 * when it closes; Esc closes it in every mode, after any pop-up opened from inside it.
 */

const OVERLAY_MEDIA = '(max-width: 1180px)';
const PHONE_MEDIA = '(max-width: 640px)';
const TAB_STEPS: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1 };
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type PanelMode = 'docked' | 'overlay' | 'sheet';

export function usePanelMode(): PanelMode {
  const isOverlay = useMediaQuery(OVERLAY_MEDIA);
  const isPhone = useMediaQuery(PHONE_MEDIA);
  if (isPhone) return 'sheet';
  return isOverlay ? 'overlay' : 'docked';
}

export function SimpleListPage({
  crumb,
  title,
  total,
  lede,
  actions,
  tabs,
  toolbar,
  notice,
  panel,
  children,
}: {
  /** "Admin · Hiring setup". */
  readonly crumb: string;
  readonly title: string;
  readonly total?: number;
  readonly lede: ReactNode;
  /** The head's one primary action ("Add a reason") — top right (06-qol §11). */
  readonly actions?: ReactNode;
  readonly tabs?: ReactNode;
  readonly toolbar?: ReactNode;
  /** A line above the table: "No reason matches…", a switched-off note. */
  readonly notice?: ReactNode;
  /** The panel, or nothing when no row is picked and nothing is being added. */
  readonly panel?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <span className={styles.crumb}>{crumb}</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          {total !== undefined && <span className={styles.total}>{total}</span>}
          <span className={styles.grow} />
          {actions}
        </div>
        <p className={styles.lede}>{lede}</p>
      </header>
      {tabs}
      {toolbar}
      {notice}
      <div className={styles.body} data-has-panel={panel ? true : undefined}>
        <div className={styles.main}>{children}</div>
        {panel}
      </div>
    </div>
  );
}

/** Search and the filter pills, on one line that wraps. */
export function ListToolbar({ search, children }: { readonly search: ReactNode; readonly children?: ReactNode }): ReactNode {
  return (
    <div className={styles.toolbar}>
      <span className={styles.search}>{search}</span>
      {children}
    </div>
  );
}

export function ListSearch({ label, value, onChange }: { readonly label: string; readonly value: string; readonly onChange: (value: string) => void }): ReactNode {
  return (
    <Input
      icon={Search}
      aria-label={label}
      placeholder={label}
      value={value}
      maxLength={100}
      onChange={(event) => onChange(event.target.value)}
      onClear={value === '' ? undefined : () => onChange('')}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && value !== '') {
          event.stopPropagation();
          onChange('');
        }
      }}
    />
  );
}

export interface PillOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly count?: number;
}

/** Filter pills with counts. Pressing the one that is on turns it off again (back to everything). */
export function FilterPills<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly PillOption<T>[];
  readonly value: T | null;
  readonly onChange: (value: T | null) => void;
}): ReactNode {
  return (
    <div className={styles.pills} role="group" aria-label={label}>
      {options.map((option) => {
        const isOn = option.value === value;
        return (
          <button key={option.value} type="button" className={styles.pill} aria-pressed={isOn} onClick={() => onChange(isOn ? null : option.value)}>
            {option.label}
            {option.count !== undefined && (
              <>
                {' '}
                <span className={styles.count}>{option.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Two lists of one page as tabs with counts ("Rejection reasons 19 · Withdrawal reasons 7"), sized to their content. */
export function ListTabs<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly PillOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}): ReactNode {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const step = TAB_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[options.indexOf(next)]?.focus();
  };
  return (
    <div className={styles.tabs} role="tablist" aria-label={label}>
      {options.map((option, index) => {
        const isOn = option.value === value;
        return (
          <button key={option.value} type="button" role="tab" className={styles.tab} aria-selected={isOn} tabIndex={isOn ? 0 : -1} onClick={() => onChange(option.value)} onKeyDown={(event) => onKeyDown(event, index)}>
            {option.label}
            {option.count !== undefined && (
              <>
                {' '}
                <span className={styles.count}>{option.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The panel. `title` is bold ink. `onClose` is Close (✕) or, on a phone, the sheet's own "Back to the list"; a panel
 * with no `onClose` is always there (Name matching's tools) and sits under the table when the screen is narrow.
 */
export function SidePanel({ mode, label, title, sub, onClose, children }: { readonly mode: PanelMode; readonly label: string; readonly title: string; readonly sub?: ReactNode; readonly onClose?: () => void; readonly children: ReactNode }): ReactNode {
  const panelRef = useRef<HTMLElement | null>(null);
  const isFloating = mode !== 'docked';

  useEffect(() => {
    if (!isFloating) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      if (opener?.isConnected) opener.focus();
    };
  }, [isFloating]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (isFloating && event.key === 'Tab') {
      keepFocusInside(event, panelRef.current);
      return;
    }
    if (event.key !== 'Escape' || event.defaultPrevented || !onClose) return;
    // A pop-up opened from inside the panel closes first.
    if (document.querySelector('[data-popover]')) return;
    event.stopPropagation();
    onClose();
  };

  return (
    <>
      {isFloating && <div className={styles.scrim} aria-hidden onClick={onClose} />}
      <aside ref={panelRef} className={styles.panel} data-mode={mode} aria-label={label} tabIndex={-1} onKeyDown={onKeyDown}>
        <header className={styles.panelHead}>
          {mode === 'sheet' && onClose && <Button variant="ghost" size="sm" icon={ArrowLeft} aria-label="Back to the list" onClick={onClose} />}
          <span className={styles.panelTitles}>
            <h2 className={styles.panelTitle}>{title}</h2>
            {sub !== undefined && <span className={styles.panelSub}>{sub}</span>}
          </span>
          {mode !== 'sheet' && onClose && <Button variant="ghost" size="sm" icon={X} aria-label="Close" onClick={onClose} />}
        </header>
        <div className={styles.panelBody}>{children}</div>
      </aside>
    </>
  );
}

/** One block of the panel, its title bold ink (CLAUDE.md §5). */
export function PanelSection({ title, children }: { readonly title?: string; readonly children: ReactNode }): ReactNode {
  return (
    <section className={styles.section} aria-label={title}>
      {title !== undefined && <h3 className={styles.sectionTitle}>{title}</h3>}
      {children}
    </section>
  );
}

/** "Stage Screening · Code SCR-SAL · Used 23 times": label over value, side by side. */
export function PanelFacts({ facts }: { readonly facts: readonly { readonly label: string; readonly value: ReactNode }[] }): ReactNode {
  return (
    <dl className={styles.facts}>
      {facts.map((fact) => (
        <div key={fact.label} className={styles.fact}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The panel's buttons, the quiet one on the left and the main one on the right. */
export function PanelActions({ children }: { readonly children: ReactNode }): ReactNode {
  return <div className={styles.actions}>{children}</div>;
}

function keepFocusInside(event: KeyboardEvent<HTMLElement>, panel: HTMLElement | null): void {
  if (!panel) return;
  if (event.target instanceof Node && !panel.contains(event.target)) return;
  const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/** Filtered to nothing (06-qol §5): says so, and clears the search and the filter. */
export function ListNoMatch({ what, query, onClear }: { readonly what: string; readonly query: string; readonly onClear: () => void }): ReactNode {
  return (
    <span className={styles.noMatch} role="status">
      {query === '' ? `No ${what} matches this filter.` : `No ${what} matches “${query}”.`}
      <Button variant="ghost" size="sm" onClick={onClear}>
        Show them all
      </Button>
    </span>
  );
}
